import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../common/Icons';
import './AdminRowActionMenu.css';

const PANEL_WIDTH = 288;
const VIEWPORT_GAP = 10;

function computePosition(anchorEl, panelEl) {
  if (!anchorEl) return { top: 0, left: 0, placement: 'bottom', width: PANEL_WIDTH };

  const panelW = Math.min(PANEL_WIDTH, window.innerWidth - VIEWPORT_GAP * 2);
  const rect = anchorEl.getBoundingClientRect();
  const panelH = panelEl?.offsetHeight || 280;
  const gap = 8;
  let placement = 'bottom';
  let top = rect.bottom + gap;
  let left = rect.right - panelW;

  if (top + panelH > window.innerHeight - VIEWPORT_GAP) {
    top = rect.top - panelH - gap;
    placement = 'top';
  }

  if (top < VIEWPORT_GAP) {
    top = VIEWPORT_GAP;
    placement = 'bottom';
  }

  left = Math.max(VIEWPORT_GAP, Math.min(window.innerWidth - panelW - VIEWPORT_GAP, left));

  return { top, left, placement, width: panelW };
}

export default function AdminRowActionMenu({
  anchorEl,
  onClose,
  ariaLabel,
  title,
  subtitle,
  initials = '?',
  badges = [],
  items = [],
  footer = null,
  extra = null,
  theme = 'blue',
  disabled = false
}) {
  const panelRef = useRef(null);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    placement: 'bottom',
    width: PANEL_WIDTH
  });

  const updatePosition = useCallback(() => {
    setPosition(computePosition(anchorEl, panelRef.current));
  }, [anchorEl]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, title, items.length]);

  useEffect(() => {
    if (!anchorEl) return undefined;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [anchorEl, updatePosition]);

  useEffect(() => {
    if (!anchorEl) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [anchorEl, onClose]);

  if (!anchorEl || !title) return null;

  return createPortal(
    <>
      <button
        type="button"
        className="adm-row-menu__backdrop"
        aria-label="Fermer le menu actions"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className={`adm-row-menu adm-row-menu--${theme} adm-row-menu--${position.placement}`}
        style={{ top: position.top, left: position.left, width: position.width }}
        role="menu"
        aria-label={ariaLabel || `Actions pour ${title}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="adm-row-menu__header">
          <div className="adm-row-menu__avatar" aria-hidden>
            {initials}
          </div>
          <div className="adm-row-menu__identity">
            <p className="adm-row-menu__name">{title}</p>
            {subtitle ? <p className="adm-row-menu__meta">{subtitle}</p> : null}
            {badges.length > 0 && (
              <div className="adm-row-menu__badges">
                {badges.map((badge) => (
                  <span
                    key={badge.id || badge.label}
                    className={`adm-row-menu__badge adm-row-menu__badge--${badge.variant || 'neutral'}`}
                  >
                    {badge.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button type="button" className="adm-row-menu__close" onClick={onClose} aria-label="Fermer">
            <Icon name="times" size={14} />
          </button>
        </header>

        {items.length > 0 && (
          <div className="adm-row-menu__body">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={`adm-row-menu__item adm-row-menu__item--${item.tone || 'primary'}`}
                disabled={disabled || item.disabled}
                onClick={item.onClick}
              >
                <span
                  className={`adm-row-menu__icon adm-row-menu__icon--${item.tone || 'primary'}`}
                  aria-hidden
                >
                  <Icon name={item.icon} size={15} />
                </span>
                <span className="adm-row-menu__item-text">
                  <span className="adm-row-menu__item-label">{item.label}</span>
                  {item.hint ? (
                    <span className="adm-row-menu__item-hint">{item.hint}</span>
                  ) : null}
                </span>
                {item.showChevron !== false && item.tone !== 'danger' ? (
                  <Icon name="chevronRight" size={12} className="adm-row-menu__chevron" />
                ) : null}
              </button>
            ))}
          </div>
        )}

        {extra ? <div className="adm-row-menu__extra">{extra}</div> : null}

        {footer ? <footer className="adm-row-menu__footer">{footer}</footer> : null}
      </div>
    </>,
    document.body
  );
}
